import React, { useState } from 'react';
import { useUsers } from '../../hooks/useUsers';
import { useAuth } from '../../hooks/useAuth';
import { UserProfile } from '../../types/user.types';

interface EditUserModalProps {
  user: UserProfile;
  onClose: () => void;
  onSuccess: () => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ user, onClose, onSuccess }) => {
  const { profile } = useAuth();
  const { updateUser } = useUsers();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    uid: user.uid,
    displayName: user.displayName || '',
    role: user.role,
    isActive: user.isActive,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Prevent editing your own role/status
      if (user.uid === profile?.uid) {
        if (formData.role !== user.role) {
          throw new Error('You cannot change your own role');
        }
        if (formData.isActive !== user.isActive) {
          throw new Error('You cannot deactivate your own account');
        }
      }

      await updateUser(formData);
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const isSuperAdmin = user.role === 'superAdmin';
  const canEditRole = profile?.role === 'superAdmin' && user.uid !== profile?.uid;
  const canEditStatus = user.uid !== profile?.uid;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Edit User</h2>
          <p className="text-sm text-gray-600 mt-1">{user.email}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {/* Error Display */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* User Info (Read-only) */}
          <div className="mb-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <p className="text-gray-500">Email:</p>
                <p className="font-medium text-gray-900">{user.email}</p>
              </div>
              <div>
                <p className="text-gray-500">UID:</p>
                <p className="font-mono text-xs text-gray-900 break-all">
                  {user.uid.substring(0, 12)}...
                </p>
              </div>
              <div>
                <p className="text-gray-500">Email Verified:</p>
                <p className={user.emailVerified ? 'text-green-600' : 'text-red-600'}>
                  {user.emailVerified ? 'Yes' : 'No'}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Created:</p>
                <p className="text-gray-900">
                  {user.createdAt?.toDate
                    ? user.createdAt.toDate().toLocaleDateString()
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Display Name */}
          <div className="mb-4">
            <label
              htmlFor="displayName"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Display Name
            </label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="John Doe"
            />
          </div>

          {/* Role */}
          <div className="mb-4">
            <label
              htmlFor="role"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Role
            </label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              disabled={!canEditRole || isSuperAdmin}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="admin">Admin</option>
              <option value="viewer">Viewer</option>
              {profile?.role === 'superAdmin' && (
                <option value="superAdmin">Super Admin</option>
              )}
            </select>
            {!canEditRole && (
              <p className="text-xs text-gray-500 mt-1">
                {user.uid === profile?.uid
                  ? 'You cannot change your own role'
                  : 'Only superAdmins can change user roles'}
              </p>
            )}
            {isSuperAdmin && user.uid !== profile?.uid && (
              <p className="text-xs text-red-500 mt-1">
                SuperAdmin roles cannot be changed. Contact another superAdmin.
              </p>
            )}
          </div>

          {/* Active Status */}
          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                disabled={!canEditStatus}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:cursor-not-allowed"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">
                Account Active
              </span>
            </label>
            {!canEditStatus && (
              <p className="text-xs text-gray-500 mt-1 ml-6">
                You cannot deactivate your own account
              </p>
            )}
            {!formData.isActive && (
              <p className="text-xs text-red-500 mt-1 ml-6">
                Warning: Deactivated users cannot log in
              </p>
            )}
          </div>

          {/* Warning for SuperAdmin */}
          {isSuperAdmin && user.uid !== profile?.uid && (
            <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>Warning:</strong> This is a SuperAdmin account. Changes
                should be made carefully to avoid locking yourself out of the system.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[100px]"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;
